import React, { useEffect, useMemo, useState } from 'react';
import {
  User,
  Mail,
  Phone,
  MessageCircle,
  Languages,
  X,
  Calendar,
  DollarSign,
  ChevronDown,
  CheckCircle,
  Edit3,
  FileText,
  PencilIcon
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSupabase } from '../context/SupabaseContext';
import { Parent, Student, Teacher, baseParent, baseStudent, baseTeacher, routes_per_type} from '../components/interfaces/All';
import { calculateDebtPerStudent } from './Wallet';

interface StudentProfileProps {
  account_type: 'student'| 'parent' | 'teacher';
}
const StudentProfile: React.FC<StudentProfileProps> = ({account_type = 'student'}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const studentsOwing = useMemo(() => {
    if (account_type !== 'teacher') return [];
    if (state.ledger == null || state.lessons == null || state.students == null) return [];
    return calculateDebtPerStudent(
      state.students.filter(s => s.id === id),
      state.lessons,
      state.ledger);
  }, []);

  const { state, fetchStudents, fetchLessons, fetchParents, fetchTeachers, updateParentData} = useSupabase();
  const [selectedStudents, setSelectedStudents] = useState<Student[]>();
  const templates = {
    student: (): Student => ( baseStudent ),
    parent: (): Parent => ( baseParent ),
    teacher: (): Teacher => (baseTeacher)
  };

  let students = [templates[account_type as keyof typeof templates]()]

  let student: Student | Parent | Teacher = templates[account_type as keyof typeof templates]()

  let recentLessons: any[] = [];
  let nextLesson: any = {};
  let totalLessons: any = 0;

  switch(account_type){
    case('student'):
      students = state.students as Student[];
      student = students.filter(s => s.id === id)[0];
      recentLessons = state.lessons.filter(l => l.student === student?.id).slice(0, 3);
      totalLessons = state.lessons.filter(l => l.student === student?.id).length;
      nextLesson = state.lessons.filter(l => l.student === student?.id && (l.status === 'confirmed' || l.status === 'pending'))[0];
    break;
    case('parent'):
      // TODO get students
      // students = state.parents as Parent[];
      student = state.parents.filter(s => s.id === id)[0];
    break;
    case('teacher'):
      // TODO get all students and lessons
    break;
  }

  useEffect(() => {
    switch(account_type){
      case('parent'):
        let parent = student as Parent
        setSelectedStudents(parent?.student_id.map(studentid => state.students.filter(s => s.id === studentid)[0]));
      break
    }
  }, []);

  const addStudentToParent = (studentId: string) => {

    if(!studentId){return}
    if(selectedStudents?.map(s => s.id).includes(studentId )){return}
    const studentdd = state.students.filter(s=>s.id == studentId)[0] as Student;

    setSelectedStudents(prev => [...prev, studentdd]);

    const updatedFormData = {
      ...student,
      student_id: selectedStudents?.map(st => st.id)
    } as Parent;
    updateParentData(student.id, updatedFormData)
  }

  const removeStudentFromParent = (studentId: string) => {
    setSelectedStudents(prev => prev?.filter(s => s.id !== studentId));
    const updatedFormData = {
      ...student,
      student_id: selectedStudents?.map(st => st.id)
    } as Parent;
    updateParentData(student.id, updatedFormData)
  }


  useEffect(() => {
    fetchLessons();
    switch(account_type){
      case 'student':
        fetchStudents();
      break;
      case('parent'):
        fetchParents();
        fetchStudents();
      break;
      case('teacher'):
        fetchTeachers();
      break;
    }
  }, []);



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20';
      case 'paused': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/20';
      case 'inactive': return 'bg-gray-100 text-gray-800 dark:bg-gray-800';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800';
    }
  };

  return (
    <div className="fade-in max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex justify-between">
        <button className='btn bg-secondary'

          onClick={() => navigate(routes_per_type[account_type] +`/${student.id}/edit`)}
        >
          <PencilIcon/>
        </button>
      </div>

      {/* Profile Header */}
      <div className="card text-center mb-8 overflow-hidden">
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-8">
          <div className="w-28 h-28 bg-primary/20 rounded-full mx-auto mb-4 flex items-center justify-center border-4 border-white/50">
            <User className="w-14 h-14 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-text mb-1">{student?.first_name}</h1>
          <span className={`inline-flex px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(student?.status)}`}>
            {student?.status.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Quick Stats */}
      { account_type == 'student' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="card p-6 text-center">
            <DollarSign className="w-8 h-8 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold text-primary">€{ studentsOwing[0]?.amountOwed.toFixed(2) || 'err' }</p>
            <p className="text-text-light text-sm">Balance Due</p>
          </div>
          <div className="card p-6 text-center">
            <Calendar className="w-8 h-8 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{totalLessons}</p>
            <p className="text-text-light text-sm">Total Lessons</p>
          </div>
          <div className="card p-6 text-center">
            {nextLesson ? (
              <>
                <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-lg font-semibold">{nextLesson.time}</p>
                <p className="text-text-light text-sm">Next Lesson</p>
              </>
            ) : (
              <>
                <Calendar className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-text-light">No upcoming lessons</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Contact Info */}
      <div className="card mb-8">
        <div className="flex items-center gap-3 p-6 pb-4 border-b border-border">
          <User className="w-6 h-6 text-primary" />
          <h2 className="text-xl font-bold">Contact Information</h2>
        </div>
        <div className="p-6 space-y-3">
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <Mail className="w-5 h-5 text-primary flex-shrink-0" />
            <span className="font-medium">{student?.email}</span>
          </div>
          {student?.phone && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <Phone className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="font-medium">{student?.phone}</span>
            </div>
          )}
        </div>
      </div>

      {/* skill & Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

        <div className="card">
          <div className="flex items-center gap-3 p-6 pb-4 border-b border-border">
            <User className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">Students</h2>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 p-6 pb-4 border-b border-border">
            <MessageCircle className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">Notes</h2>
          </div>
          <div className="p-6">
            <p className="text-text-light whitespace-pre-wrap">{student?.notes}</p>
          </div>
        </div>
      </div>

      {/* Recent Lessons */}
      {account_type == 'student' && (
        <div className="card">
          <div className="flex items-center justify-between p-6 pb-4 border-b border-border">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-primary" />
              <h2 className="text-xl font-bold">Recent Lessons</h2>
            </div>
            <button
              className="btn btn-outline text-sm px-4"
              onClick={() => navigate(`/lesson?student=${student?.id}`)}
            >
              View All
            </button>
          </div>
          <div className="p-6">
            {recentLessons.length === 0 ? (
              <p className="text-text-light text-center py-8">No recent lessons</p>
            ) : (
              <div className="space-y-3">
                {recentLessons.slice(0, 3).map((lesson) => (
                  <div key={lesson.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <div>
                      <p className="font-semibold">{lesson.date}</p>
                      <p className="text-sm text-text-light">{lesson.duration}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      lesson.status === 'completed'
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800'
                    }`}>
                      {lesson.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {account_type === 'parent' && (
        <div className="card">
          <div className="flex items-center justify-between p-6 pb-4 border-b border-border">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-primary" />
              <h2 className="text-xl font-bold">Students' list</h2>
            </div>
          </div>
          <div className="p-6">
            {/* Students Dropdown */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-text-light mb-3">
                Add Students
              </label>
              <div className="relative">
                <select
                  className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent appearance-none bg-white dark:bg-gray-800"
                  onChange={(e) => {
                    addStudentToParent(e.target.value)
                    e.target.value = ''; // Reset dropdown
                  }}
                >
                  <option value="">Select a student...</option>
                  {state.students.map((studentdd) => (
                    <option
                      key={studentdd.id}
                      value={studentdd.id}
                      disabled={selectedStudents?.includes(studentdd)}
                    >
                      {studentdd?.first_name + ' ' + studentdd?.last_name }
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <ChevronDown className="w-5 h-5" />
                </div>
              </div>
            </div>

            {/* Selected Students List */}
            {selectedStudents?.length === 0 ? (
              <p className="text-text-light text-center py-8">No students selected</p>
            ) : (
              <div className="space-y-3">
                {selectedStudents?.map((studentdd) => {
                  return (
                    <div key={studentdd?.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <div>
                        <p className="font-semibold">
                          {studentdd?.first_name + ' ' + studentdd?.last_name}
                        </p>
                        <p className="text-sm text-text-light">{studentdd?.first_name + ' ' + studentdd?.last_name}</p>
                      </div>
                      <button
                        onClick={() => removeStudentFromParent(studentdd.id)}
                        className="text-gray-500 hover:text-red-500 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-4 mt-8">
        <button
          className="btn btn-primary py-4 text-sm flex justify-left"
          onClick={() => navigate(`/students/${student.id}/ledger`)}
        >
          <DollarSign className="w-5 h-5 mr-2" />
          View Ledger
        </button>
        <button
          className="btn btn-outline py-4 text-sm flex justify-left"
          onClick={() => navigate(`/students/${student.id}/edit`)}
        >
          <Edit3 className="w-5 h-5 mr-2" />
          Edit Profile
        </button>
      </div>
    </div>
  );
};

export default StudentProfile;
